import { CircularProgress } from '@material-ui/core'
import { makeStyles } from '@material-ui/styles'
import React, { memo, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { VariableSizeList } from 'react-window'
import GrinMenuItem from '../menu/GrinMenuItem'
import GrinTag from './GrinTag'

const DEFAULT_ROW_HEIGHT = 34
const MAX_LIST_HEIGHT = 240
const SCROLL_LOAD_MORE_THRESHOLD = 0.8

const useStyles = makeStyles({
  tagContainer: {
    '&&': {
      paddingTop: 5,
      paddingBottom: 5,
      minHeight: 0
    },
    '& > *': {
      minWidth: 0,
      maxWidth: 300
    },
    '& .MuiChip-label': {
      minWidth: 0
    },
    '& .MuiChip-deleteIconSmall': {
      flexShrink: 0
    }
  },
  tagClass: {
    maxWidth: 300,
    overflow: 'hidden'
  },
  loaderContainer: {
    display: 'flex',
    justifyContent: 'center',
    padding: '6px 0'
  }
})

const Row = memo(({ index, style, data }) => {
  const rowRef = useRef(null)
  const { tags, onTagSelected, onDeleteTag, tagContainerClass, tagClass, setRowHeight } = data
  const tag = tags[index]

  useLayoutEffect(() => {
    if (rowRef.current) {
      setRowHeight(index, rowRef.current.offsetHeight)
    }
  }) // intentionally no deps — re-measure on every render in case width changes

  return (
    <div style={style}>
      <div ref={rowRef}>
        <GrinMenuItem nonActionable className={tagContainerClass}>
          <GrinTag
            tag={tag}
            onClick={() => onTagSelected(tag)}
            onDelete={() => onDeleteTag(tag)}
            automationsPrefix="tags-picker"
            className={tagClass}
          />
        </GrinMenuItem>
      </div>
    </div>
  )
})

const TagsPickerList = ({ tags, onTagSelected, onDeleteTag, onLoadMore, isLoadingMore, hasMore }) => {
  const classes = useStyles()
  const listRef = useRef(null)
  const rowHeights = useRef({})
  const [, forceUpdate] = useState(0)

  // Guard ref prevents duplicate onLoadMore() calls between a dispatch and the
  // subsequent React re-render (stale-closure window where isLoadingMore is still false).
  const loadMoreTriggeredRef = useRef(false)

  // Reset the guard when loading finishes (isLoadingMore transitions true → false),
  // meaning new data has arrived and the user may scroll further to trigger the next page.
  const prevIsLoadingMore = useRef(isLoadingMore)
  useEffect(() => {
    if (prevIsLoadingMore.current && !isLoadingMore) {
      loadMoreTriggeredRef.current = false
    }
    prevIsLoadingMore.current = isLoadingMore
  }, [isLoadingMore])

  useEffect(() => {
    rowHeights.current = {}
    loadMoreTriggeredRef.current = false
    if (listRef.current) {
      listRef.current.resetAfterIndex(0)
    }
  }, [tags])

  const setRowHeight = useCallback((index, height) => {
    if (rowHeights.current[index] !== height) {
      rowHeights.current[index] = height
      if (listRef.current) {
        listRef.current.resetAfterIndex(index, false)
      }
      forceUpdate(n => n + 1)
    }
  }, [])

  const getItemSize = useCallback(index => rowHeights.current[index] || DEFAULT_ROW_HEIGHT, [])

  const totalHeight = tags.reduce((acc, _, index) => acc + (rowHeights.current[index] || DEFAULT_ROW_HEIGHT), 0)
  const listHeight = Math.min(MAX_LIST_HEIGHT, totalHeight)

  // Keep dimension refs in sync for the onScroll handler (avoids stale closures)
  const totalHeightRef = useRef(totalHeight)
  const listHeightRef = useRef(listHeight)
  totalHeightRef.current = totalHeight
  listHeightRef.current = listHeight

  // Edge case: if every loaded item fits inside the viewport (no scrollbar) but more
  // data exists, treat it as "100 % scrolled" and trigger a load automatically.
  // In practice this is extremely rare (page size 100, viewport fits ~7 rows) but
  // guards against edge cases where the first page is very small.
  useEffect(() => {
    if (
      tags.length > 0 &&
      hasMore &&
      !isLoadingMore &&
      !loadMoreTriggeredRef.current &&
      totalHeight <= MAX_LIST_HEIGHT
    ) {
      loadMoreTriggeredRef.current = true
      onLoadMore?.()
    }
  }, [tags.length, hasMore, isLoadingMore, totalHeight, onLoadMore])

  const handleScroll = useCallback(
    ({ scrollOffset }) => {
      if (loadMoreTriggeredRef.current) return

      const scrollableHeight = totalHeightRef.current - listHeightRef.current
      if (scrollableHeight <= 0) return

      const scrollPercentage = scrollOffset / scrollableHeight
      if (scrollPercentage >= SCROLL_LOAD_MORE_THRESHOLD) {
        loadMoreTriggeredRef.current = true
        onLoadMore?.()
      }
    },
    [onLoadMore]
  )

  const itemData = useMemo(
    () => ({
      tags,
      onTagSelected,
      onDeleteTag,
      tagContainerClass: classes.tagContainer,
      tagClass: classes.tagClass,
      setRowHeight
    }),
    [tags, onTagSelected, onDeleteTag, classes.tagContainer, classes.tagClass, setRowHeight]
  )

  return (
    <>
      <VariableSizeList
        ref={listRef}
        height={listHeight}
        itemCount={tags.length}
        itemSize={getItemSize}
        width="100%"
        itemData={itemData}
        onScroll={handleScroll}
      >
        {Row}
      </VariableSizeList>
      {isLoadingMore && (
        <div className={classes.loaderContainer}>
          <CircularProgress size={16} />
        </div>
      )}
    </>
  )
}

export default memo(TagsPickerList)
